import API_BASE_URL from './baseUrl';
async function loadLegacyAuthRuntimeConfig() {
    const [providersResult, emailCodeResult, passkeysResult] = await Promise.allSettled([
        request(`${API_BASE_URL}/auth/providers`),
        request(`${API_BASE_URL}/auth/email-code/config`),
        request(`${API_BASE_URL}/auth/passkeys/config`),
    ]);
    if (emailCodeResult.status === 'rejected' || passkeysResult.status === 'rejected') {
        throw new Error('Sign-in capability endpoints are unavailable');
    }
    return {
        success: true,
        providers: providersResult.status === 'fulfilled' ? providersResult.value.providers || {} : {},
        emailCode: emailCodeResult.value.emailCode,
        passkeys: passkeysResult.value.passkeys,
        fastSwitchAvailable: false,
    };
}
async function loadAuthRuntimeConfig() {
    try {
        return await request(`${API_BASE_URL}/auth/config`);
    }
    catch (primaryError) {
        try {
            return await loadLegacyAuthRuntimeConfig();
        }
        catch (_legacyError) {
            throw primaryError;
        }
    }
}
const getCsrfToken = () => {
    if (typeof document === 'undefined') {
        return null;
    }
    const match = document.cookie.match(/(?:^|;\s*)_csrfToken=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
};
const request = async (url, init) => {
    const method = init?.method?.toUpperCase() ?? 'GET';
    const csrfToken = method === 'GET' || method === 'HEAD' ? null : getCsrfToken();
    const response = await fetch(url, {
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
            ...init?.headers,
        },
        ...init,
    });
    if (!response.ok) {
        let errorMessage = `Auth request failed: ${response.status}`;
        try {
            const payload = (await response.json());
            if (payload?.message || payload?.error) {
                errorMessage = payload.message || payload.error || errorMessage;
            }
        }
        catch (_err) {
            // Keep default status-based error message when no JSON payload is returned.
        }
        throw new Error(errorMessage);
    }
    return response.json();
};
export const authApi = {
    login: (payload) => request(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        body: JSON.stringify(payload),
    }),
    signup: (payload) => request(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        body: JSON.stringify(payload),
    }),
    logout: () => request(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
    }),
    me: async () => {
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (response.status === 401) {
            return { success: false, user: null, activeRole: 'reader' };
        }
        if (!response.ok) {
            throw new Error(`Auth request failed: ${response.status}`);
        }
        return response.json();
    },
    roleSwitch: (role) => request(`${API_BASE_URL}/auth/role-switch`, {
        method: 'POST',
        body: JSON.stringify({ role }),
    }),
    onboarding: () => request(`${API_BASE_URL}/auth/onboarding`),
    completeOnboarding: (track, acknowledgements) => request(`${API_BASE_URL}/auth/onboarding/${encodeURIComponent(track)}/complete`, {
        method: 'POST',
        body: JSON.stringify({ acknowledgements, confirmation: true }),
    }),
    providers: () => request(`${API_BASE_URL}/auth/providers`),
    config: loadAuthRuntimeConfig,
    emailCodeConfig: async () => {
        const result = await request(`${API_BASE_URL}/auth/email-code/config`);
        return result.emailCode;
    },
    requestEmailCode: (payload) => request(`${API_BASE_URL}/auth/email-code/request`, {
        method: 'POST',
        body: JSON.stringify(payload),
    }),
    verifyEmailCode: (payload) => request(`${API_BASE_URL}/auth/email-code/verify`, {
        method: 'POST',
        body: JSON.stringify(payload),
    }),
    completeEmailCodeSignup: (payload) => request(`${API_BASE_URL}/auth/email-code/complete`, {
        method: 'POST',
        body: JSON.stringify(payload),
    }),
    forgotPassword: (email) => request(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        body: JSON.stringify({ email }),
    }),
    resetPassword: (email, token, password) => request(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ email, token, password }),
    }),
    verificationStatus: () => request(`${API_BASE_URL}/auth/verification-status`),
    resendVerification: (email) => request(`${API_BASE_URL}/auth/verification-resend`, {
        method: 'POST',
        body: JSON.stringify({ email }),
    }),
    confirmVerification: (token) => request(`${API_BASE_URL}/auth/verification-confirm`, {
        method: 'POST',
        body: JSON.stringify({ token }),
    }),
    fastSwitch: (pin) => request(`${API_BASE_URL}/auth/fast-switch`, {
        method: 'POST',
        body: JSON.stringify({ pin }),
    }),
    accountSettings: () => request(`${API_BASE_URL}/auth/account-settings`),
    updateAccountContact: (payload) => request(`${API_BASE_URL}/auth/account-settings/contact`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    }),
    updateAccountIdentity: (payload) => request(`${API_BASE_URL}/auth/account-settings/identity`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    }),
    updateAccountPassword: (payload) => request(`${API_BASE_URL}/auth/account-settings/password`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    }),
    sessions: () => request(`${API_BASE_URL}/auth/sessions`),
    revokeSession: (id) => request(`${API_BASE_URL}/auth/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    revokeOtherSessions: () => request(`${API_BASE_URL}/auth/sessions/revoke-others`, { method: 'POST', body: '{}' }),
};
export default authApi;
